import { apiFetch } from '../../lib/api'
import type { DocumentRef } from '../../lib/documentReader'
import type { BookActionsResponse } from './bookReviewApi'
import { bookReviewQuery, type BookReviewScope } from './bookReviewQuery'
import {
  matchingReceiptMatches,
  matchingUrl,
  type CapitalMatchingReview,
  type CapitalMatchCommand,
  type CapitalMatchReceipt,
} from './capitalMatching'

export interface HeldBookItem {
  item_id: string
  group_id: string
  document_id: string
  assessment_id?: string | null
  kind: string
  state: string
  revision: string
  messages: readonly string[]
  request_draft_available?: boolean
  actions_available: false
  financial_authority: false
}
export interface HeldQuestionReview {
  item_id: string
  revision: string
  assessment_id: string | null
  complete: boolean
  financial_authority: false
  questions: readonly {
    field: string
    area: 'capital' | 'cash'
    prompt: string
    reason: string
    citations: readonly {
      document_id: string
      content_sha256: string
      revision: string
      page: number
      quote: string
    }[]
    documents: readonly DocumentRef[]
  }[]
}
export interface SavedHeldRequest {
  request_id: string
  item_id: string
  requested_document: string
  counterparty_name: string
  earlier_review: boolean
  status: 'draft'
  outbound_available: false
}
export interface SavedHeldDismissal {
  dismissal_id: string
  item_id: string
  actor_id: string
  reason: string
  revision: string
  earlier_review: boolean
  engine_hold_unchanged: true
  financial_authority: false
  evidence_waived: false
}
export interface HeldSourceInterpretationOption {
  item_id: string
  field: string
  answer: 'call_only_no_standing_balance'
  label: string
  citation: HeldQuestionReview['questions'][number]['citations'][number]
  source_sha256: string
  source_position_fingerprint: string
  financial_authority: false
}
export const SOURCE_INTERPRETATION_LABEL =
  'This notice states only a call; no standing balance is stated.'
// Sent by the server when a published call's source question is open again.
export const SOURCE_ANSWER_REOPENED_MESSAGE =
  'The earlier source answer no longer proves this question. Answer it again to continue. Nothing is booked by this answer.'
export interface BookWorkResponse {
  group_id: string
  scope: { fund_id?: string | null; client_id?: string | null }
  ordinary: BookActionsResponse | null
  matching: readonly CapitalMatchingReview[]
  unavailable?: { proposal_ids: readonly string[]; reason: string } | null
  documents: readonly DocumentRef[]
  held_items?: readonly HeldBookItem[]
  held_questions?: readonly HeldQuestionReview[]
  held_dismissal_available_ids?: readonly string[]
  held_dismissal_authors?: readonly { actor_id: string; display_name: string }[]
  saved_held_requests?: { requests: readonly SavedHeldRequest[]; complete: boolean }
  saved_held_dismissals?: { dismissals: readonly SavedHeldDismissal[]; complete: boolean }
  held_source_interpretation_options?: readonly HeldSourceInterpretationOption[]
}
export function bookWorkSelectionMatches(
  result: BookWorkResponse,
  groupId: string,
  proposalIds: readonly string[],
  scope: BookReviewScope = {},
  heldItemIds: readonly string[] = []
): boolean {
  const represented = [
    ...result.matching.map((item) => item.proposal_id),
    ...(result.ordinary?.review.objects
      .filter((item) => item.role !== 'effect')
      .map((item) => item.proposal_id) ?? []),
    ...(result.unavailable?.proposal_ids ?? []),
  ]
  const wanted = new Set(proposalIds)
  const held = result.held_items ?? []
  const wantedHeld = new Set(heldItemIds)
  return (
    result.group_id === groupId &&
    held.length === wantedHeld.size &&
    new Set(held.map((item) => item.item_id)).size === held.length &&
    held.every(
      (item) =>
        wantedHeld.has(item.item_id) &&
        item.group_id === groupId &&
        item.financial_authority === false &&
        item.actions_available === false
    ) &&
    (result.saved_held_requests === undefined ||
      (typeof result.saved_held_requests.complete === 'boolean' &&
        result.saved_held_requests.requests.length <= 50 &&
        (result.saved_held_requests.complete || result.saved_held_requests.requests.length === 0) &&
        new Set(result.saved_held_requests.requests.map((row) => row.request_id)).size ===
          result.saved_held_requests.requests.length &&
        result.saved_held_requests.requests.every(
          (row) =>
            Boolean(result.scope.fund_id) !== Boolean(result.scope.client_id) &&
            wantedHeld.has(row.item_id) &&
            row.status === 'draft' &&
            row.outbound_available === false &&
            typeof row.earlier_review === 'boolean' &&
            typeof row.requested_document === 'string' &&
            typeof row.counterparty_name === 'string'
        ))) &&
    heldQuestionsMatch(result, held) &&
    savedDismissalsMatch(result, held) &&
    dismissalActionsMatch(result, held) &&
    sourceInterpretationOptionsMatch(result, held) &&
    dismissalAuthorsMatch(result) &&
    represented.length === new Set(represented).size &&
    represented.length === wanted.size &&
    represented.every((id) => wanted.has(id)) &&
    result.matching.every((item) => item.group_id === groupId) &&
    (!scope.fundId || result.scope.fund_id === scope.fundId) &&
    (!scope.clientId || result.scope.client_id === scope.clientId)
  )
}
function heldQuestionsMatch(result: BookWorkResponse, held: readonly HeldBookItem[]): boolean {
  const reviews = result.held_questions
  if (reviews === undefined) return true // Older API: no verified question claim.
  if (!Array.isArray(reviews) || reviews.length !== held.length) return false
  const ids = new Set<string>()
  return reviews.every((review) => {
    if (!review) return false
    const item = held.find((row) => row.item_id === review.item_id)
    if (
      !item ||
      ids.has(review.item_id) ||
      review.revision !== item.revision ||
      review.financial_authority !== false ||
      typeof review.complete !== 'boolean' ||
      !Array.isArray(review.questions) ||
      (review.complete &&
        (item.state !== 'needs_review' ||
          !review.assessment_id ||
          review.assessment_id !== item.assessment_id ||
          !review.questions.length)) ||
      (!review.complete && review.questions.length)
    )
      return false
    ids.add(review.item_id)
    return review.questions.every(
      (question: HeldQuestionReview['questions'][number]) =>
        question &&
        typeof question.field === 'string' &&
        Boolean(question.field) &&
        ['capital', 'cash'].includes(question.area) &&
        typeof question.prompt === 'string' &&
        Boolean(question.prompt.trim()) &&
        typeof question.reason === 'string' &&
        Boolean(question.reason.trim()) &&
        Array.isArray(question.citations) &&
        question.citations.length > 0 &&
        Array.isArray(question.documents) &&
        question.documents.length === question.citations.length &&
        question.citations.every(
          (
            citation: HeldQuestionReview['questions'][number]['citations'][number],
            index: number
          ) => {
            const ref = question.documents[index]
            return (
              citation &&
              ref &&
              citation.document_id === ref.document_id &&
              citation.page === ref.page &&
              citation.quote === ref.snippet &&
              Number.isSafeInteger(citation.page) &&
              citation.page > 0 &&
              typeof citation.quote === 'string' &&
              Boolean(citation.quote.trim()) &&
              typeof ref.display_title === 'string' &&
              Boolean(ref.display_title.trim()) &&
              /^[a-f0-9]{64}$/.test(citation.content_sha256) &&
              /^[a-f0-9]{64}$/.test(citation.revision)
            )
          }
        )
    )
  })
}

// An answer control exists only for a current, complete, single-cited question of
// this exact selection; anything else leaves the whole response unusable.
function sourceInterpretationOptionsMatch(
  result: BookWorkResponse,
  held: readonly HeldBookItem[]
): boolean {
  const options = result.held_source_interpretation_options
  if (options === undefined) return true
  if (!Array.isArray(options) || options.length > 50) return false
  const seen = new Set<string>()
  return options.every((option) => {
    if (!option || !result.scope.fund_id) return false
    const item = held.find((row) => row.item_id === option.item_id)
    const review = result.held_questions?.find(
      (row) => row.item_id === option.item_id && row.revision === item?.revision
    )
    const question = review?.complete
      ? review.questions.filter((row) => row.field === option.field)
      : []
    const key = `${option.item_id}:${option.field}`
    const cited = question.length === 1 ? question[0].citations : []
    if (
      !item ||
      item.state !== 'needs_review' ||
      seen.has(key) ||
      !/^source_positions\[(0|[1-9][0-9]{0,3})\]$/.test(option.field) ||
      option.answer !== 'call_only_no_standing_balance' ||
      option.label !== SOURCE_INTERPRETATION_LABEL ||
      option.financial_authority !== false ||
      typeof option.source_position_fingerprint !== 'string' ||
      !option.source_position_fingerprint ||
      !/^[a-f0-9]{64}$/.test(option.source_sha256) ||
      !option.citation ||
      option.citation.content_sha256 !== option.source_sha256 ||
      option.citation.document_id !== item.document_id ||
      cited.length !== 1 ||
      cited[0].document_id !== option.citation.document_id ||
      cited[0].content_sha256 !== option.citation.content_sha256 ||
      cited[0].revision !== option.citation.revision ||
      cited[0].page !== option.citation.page ||
      cited[0].quote !== option.citation.quote
    )
      return false
    seen.add(key)
    return true
  })
}

function dismissalAuthorsMatch(result: BookWorkResponse): boolean {
  const authors = result.held_dismissal_authors
  if (authors === undefined) return true
  if (!Array.isArray(authors) || authors.length > 50) return false
  const ids = new Set<string>()
  return authors.every((row) => {
    if (
      !row ||
      !result.scope.fund_id ||
      result.saved_held_dismissals?.complete !== true ||
      !result.saved_held_dismissals.dismissals.some(
        (receipt) => receipt.actor_id === row.actor_id
      ) ||
      ids.has(row.actor_id) ||
      typeof row.display_name !== 'string' ||
      !row.display_name.trim() ||
      Array.from(row.display_name).length > 255
    )
      return false
    ids.add(row.actor_id)
    return true
  })
}

function dismissalActionsMatch(result: BookWorkResponse, held: readonly HeldBookItem[]): boolean {
  const ids = result.held_dismissal_available_ids
  if (ids === undefined) return true
  if (!Array.isArray(ids) || ids.length > 50 || new Set(ids).size !== ids.length) return false
  return ids.every(
    (id) =>
      Boolean(result.scope.fund_id) &&
      result.saved_held_dismissals?.complete === true &&
      held.some((item) => item.item_id === id && item.state === 'needs_review') &&
      !result.saved_held_dismissals.dismissals.some(
        (row) => row.item_id === id && !row.earlier_review
      )
  )
}

function savedDismissalsMatch(result: BookWorkResponse, held: readonly HeldBookItem[]): boolean {
  const saved = result.saved_held_dismissals
  if (saved === undefined) return true
  if (
    !saved ||
    typeof saved.complete !== 'boolean' ||
    !Array.isArray(saved.dismissals) ||
    saved.dismissals.length > 50 ||
    (!saved.complete && saved.dismissals.length !== 0)
  )
    return false
  const ids = new Set<string>()
  return saved.dismissals.every((row) => {
    if (!row || !result.scope.fund_id) return false
    const item = held.find((candidate) => candidate.item_id === row.item_id)
    if (
      !item ||
      typeof row.dismissal_id !== 'string' ||
      !row.dismissal_id ||
      ids.has(row.dismissal_id) ||
      typeof row.actor_id !== 'string' ||
      !row.actor_id ||
      typeof row.reason !== 'string' ||
      !row.reason.trim() ||
      row.reason.length > 2000 ||
      typeof row.revision !== 'string' ||
      !row.revision ||
      row.earlier_review !== (row.revision !== item.revision) ||
      row.engine_hold_unchanged !== true ||
      row.financial_authority !== false ||
      row.evidence_waived !== false
    )
      return false
    ids.add(row.dismissal_id)
    return true
  })
}

export function bookWorkQuery(
  groupId: string,
  proposalIds: readonly string[],
  scope: BookReviewScope = {},
  heldItemIds: readonly string[] = []
): string {
  const held = [...new Set(heldItemIds)]
  if (
    !groupId ||
    held.length !== heldItemIds.length ||
    held.length > 50 ||
    (!proposalIds.length && !held.length)
  )
    throw new Error('Choose the Book items to review')
  let url: string
  if (proposalIds.length)
    url = bookReviewQuery(groupId, proposalIds, scope).replace('/book-review?', '/book-work?')
  else {
    const query = new URLSearchParams()
    if (scope.fundId) query.set('fund_id', scope.fundId)
    else if (scope.clientId) query.set('client_id', scope.clientId)
    url = '/api/rk/decks/group/' + encodeURIComponent(groupId) + '/book-work?' + query.toString()
  }
  for (const item of held) url += '&held_item_id=' + encodeURIComponent(item)
  return url
}

export async function fetchBookWork(
  groupId: string,
  proposalIds: readonly string[],
  scope: BookReviewScope = {},
  background = false,
  heldItemIds: readonly string[] = []
): Promise<BookWorkResponse> {
  const url = bookWorkQuery(groupId, proposalIds, scope, heldItemIds)
  const result = await apiFetch<BookWorkResponse>(url, { background })
  if (!bookWorkSelectionMatches(result, groupId, proposalIds, scope, heldItemIds))
    throw new Error('This Book review belongs to a different selection.')
  return result
}
export async function submitCapitalMatch(
  review: CapitalMatchingReview,
  command: CapitalMatchCommand
): Promise<CapitalMatchReceipt> {
  const receipt = await apiFetch<CapitalMatchReceipt>(matchingUrl(review), {
    method: 'POST',
    body: JSON.stringify(command),
  })
  if (!matchingReceiptMatches(review, command, receipt))
    throw new Error('The matching response has no confirmed Record receipt. Refresh the review.')
  return receipt
}
